import { GeoJsonProperties, LineString, Position } from "geojson";
import { buildEmptyCreateHlaWay, HlaWayType } from "../../../hla/models/HlaWay";
import { categorizeCompositeFeature } from "../../utils/query/categorizeCompositeFeature";
import turf from "../../../shared/utils/turf";
import {
  delay,
  FacilityType,
  FacilityWidth,
  FlowDirectionType,
  HeavyVehicleFlowType,
} from "@keita/lanepatrol-sdk";
import {
  buildCreateOrUpdateHlaLane,
  PresenceDistance,
} from "../../../hla/models/HlaLane";
import { findAdjacentFeatures } from "../../utils/query/findAdjacentFeatures";
import { applyAdjacentFeaturesToLane } from "../../utils/query/applyAdjacentFeaturesToLane";

export async function createWayFromLineFeature(
  featureId: number,
  coordinates: Position[],
  properties: GeoJsonProperties,
  enabledCategories: HlaWayType[],
  map: mapboxgl.Map,
  options: {
    frontSide: "left" | "right";
    backSide: "left" | "right";
    rightHandTraffic: boolean;
  },
) {
  const props = properties!;
  const type = props.type ?? "";
  const className = props.class ?? "";

  var categoryName = categorizeCompositeFeature(className, type);

  if (!enabledCategories.includes(categoryName)) {
    return;
  }

  const lineString: LineString = {
    type: "LineString",
    coordinates,
  };

  console.log("Waiting and moving...");
  const featureCenter = turf.centroid(lineString);
  map.jumpTo({
    center: featureCenter.geometry.coordinates as mapboxgl.LngLatLike,
    zoom: 16,
  });
  await delay(200);

  const way = buildEmptyCreateHlaWay();

  console.log("Importing", categoryName, props);

  way.coordinates = coordinates;
  way.lengthM = turf.length(
    {
      type: "Feature",
      geometry: lineString,
      properties: null,
    },
    {
      units: "meters",
    },
  );

  way.type = categoryName;

  // Name
  if (props.name) {
    way.name = props.name;
  }

  way.frontFacingLane = buildCreateOrUpdateHlaLane(true);

  // Flow direction
  if (props.oneway) {
    way.frontFacingLane.flowDirection =
      props.oneway === "true"
        ? FlowDirectionType.OneWay
        : FlowDirectionType.TwoWay;
  }

  if (
    categoryName === HlaWayType.Motorway ||
    categoryName === HlaWayType.Primary ||
    categoryName === HlaWayType.Secondary ||
    categoryName === HlaWayType.Tertiary
  ) {
    way.heavyVehicleFlow = HeavyVehicleFlowType.MediumToHigh;
  }

  // Facility type and width
  if (categoryName === HlaWayType.Cycleway) {
    way.frontFacingLane.facilityType = FacilityType.OffRoadBicyclePath;
  } else if (
    categoryName === HlaWayType.Service ||
    categoryName === HlaWayType.Motorway ||
    categoryName === HlaWayType.Primary ||
    categoryName === HlaWayType.Secondary ||
    categoryName === HlaWayType.Tertiary ||
    categoryName === HlaWayType.Unclassified
  ) {
    way.frontFacingLane.facilityType = FacilityType.MixedTrafficRoadLane;
    way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Wide;

    // if the flow is mixed with the road, there's a road lane to the side
    if (options.rightHandTraffic) {
      way.frontFacingLane.adjacentFeaturesLeft.roadLane =
        PresenceDistance.From0to1m;
    } else {
      way.frontFacingLane.adjacentFeaturesRight.roadLane =
        PresenceDistance.From0to1m;
    }
  } else if (categoryName === HlaWayType.Sidewalk) {
    way.frontFacingLane.facilityType = FacilityType.Sidewalk;
    way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
  } else if (
    categoryName === HlaWayType.Pedestrian ||
    categoryName === HlaWayType.Platform ||
    categoryName === HlaWayType.FootPath
  ) {
    way.frontFacingLane.facilityType = FacilityType.MultiUsePath;
    if (categoryName === HlaWayType.Pedestrian) {
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Wide;
    } else {
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.VeryNarrow;
    }
  }

  const roadWithCyclingInfrastructure =
    !!props.bike_lane && props.bike_lane !== "no";

  if (roadWithCyclingInfrastructure) {
    if (props.bike_lane !== options.backSide) {
      way.frontFacingLane.facilityType = FacilityType.OnRoadBicycleLane;
      way.frontFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
      way.frontFacingLane.flowDirection = FlowDirectionType.OneWay;

      if (options.rightHandTraffic) {
        way.frontFacingLane.adjacentFeaturesLeft.roadLane =
          PresenceDistance.From0to1m;
      } else {
        way.frontFacingLane.adjacentFeaturesRight.roadLane =
          PresenceDistance.From0to1m;
      }
    }

    if (props.bike_lane !== options.frontSide) {
      way.backFacingLane = buildCreateOrUpdateHlaLane(false);
      way.backFacingLane.facilityType = FacilityType.OnRoadBicycleLane;
      way.backFacingLane.facilityWidthPerDirection = FacilityWidth.Narrow;
      way.backFacingLane.flowDirection = FlowDirectionType.OneWay;

      if (options.rightHandTraffic) {
        way.backFacingLane.adjacentFeaturesLeft.roadLane =
          PresenceDistance.From0to1m;
      } else {
        way.backFacingLane.adjacentFeaturesRight.roadLane =
          PresenceDistance.From0to1m;
      }
    }
  }

  const adjacentFeatures = findAdjacentFeatures(featureId, lineString, map);

  console.log("Distances to each adjacent feature type:", adjacentFeatures);

  if (adjacentFeatures.BusStop.left || adjacentFeatures.BusStop.right) {
    way.heavyVehicleFlow = HeavyVehicleFlowType.MediumToHigh;
  }

  if (way.frontFacingLane) {
    applyAdjacentFeaturesToLane(way.frontFacingLane, adjacentFeatures);
  }

  if (way.backFacingLane) {
    applyAdjacentFeaturesToLane(way.backFacingLane, adjacentFeatures);
  }

  return way;
}
